import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  formatBillingCurrency,
  formatBillingDate,
  getBillingStatusMeta
} from '../utils/franchiseBilling';
import { 
  User, 
  Lock, 
  Edit, 
  Save, 
  X, 
  Bell, 
  Download, 
  Award, 
  CreditCard, 
  Settings,
  Trash2,
  Calendar,
  CheckCircle
} from 'lucide-react';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isManagingNotifications, setIsManagingNotifications] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isViewingProgress, setIsViewingProgress] = useState(false);
  const [isViewingBilling, setIsViewingBilling] = useState(false);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [isCancellationWarning, setIsCancellationWarning] = useState(false);
  
  // Form states
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    avatar: user?.avatar || '',
    phone: user?.phone || '',
    gender: user?.gender || '',
    address: user?.address || '',
    dateOfBirth: user?.dateOfBirth ? user.dateOfBirth.split('T')[0] : ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    courseUpdates: true,
    promotionalEmails: false,
    pushNotifications: true
  });

  const [emailData, setEmailData] = useState({
    currentPassword: '',
    newEmail: ''
  });

  const [learningProgress] = useState({
    completedCourses: 0,
    totalHours: 0,
    certificates: 0,
    achievements: 0
  });

  const [billingInfo] = useState({
    paymentMethods: [],
    recentTransactions: [],
    subscription: { plan: '', amount: 0, nextBilling: '' }
  });

  // Subscription management state
  const [subscriptionData] = useState({
    currentPlan: {
      name: '',
      price: 0,
      features: [],
      nextBilling: '',
      status: 'inactive'
    },
    availablePlans: []
  });

  const [franchiseBilling, setFranchiseBilling] = useState(null);
  const [loadingFranchiseBilling, setLoadingFranchiseBilling] = useState(false);

  // Update profile data when user changes
  React.useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        bio: user.bio || '',
        avatar: user.avatar || '',
        phone: user.phone || '',
        gender: user.gender || '',
        address: user.address || '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : ''
      });
    }
  }, [user]);

  React.useEffect(() => {
    const fetchFranchiseBilling = async () => {
      if (!user?.isFranchise) {
        setFranchiseBilling(null);
        return;
      }

      try {
        setLoadingFranchiseBilling(true);
        const response = await axios.get('/api/users/franchise-billing');
        setFranchiseBilling(response.data);
      } catch (error) {
        console.error('Failed to fetch franchise billing:', error);
        setFranchiseBilling(null);
      } finally {
        setLoadingFranchiseBilling(false);
      }
    };

    fetchFranchiseBilling();
  }, [user?.isFranchise, user?._id]);

  const handleProfileUpdate = async () => {
    try {
      await updateProfile(profileData);
      setIsEditing(false);
    } catch (error) {
      console.error('Profile update failed:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Password change failed:', error);
    }
  };

  const handleEmailChange = async () => {
    try {
      // This would be implemented in the backend
      console.log('Email change requested:', emailData);
      setIsChangingEmail(false);
      setEmailData({ currentPassword: '', newEmail: '' });
    } catch (error) {
      console.error('Email change failed:', error);
    }
  };

  const handleAccountDeletion = async () => {
    if (window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없으며, 모든 데이터가 영구적으로 삭제됩니다. 계속하시겠습니까?')) {
      try {
        // This would be implemented in the backend
        console.log('Account deletion requested');
        setIsDeletingAccount(false);
      } catch (error) {
        console.error('Account deletion failed:', error);
      }
    }
  };

  const handleSubscriptionChange = (planId) => {
    // This would be implemented in the backend
    console.log('Subscription change requested:', planId);
    alert('구독 변경이 요청되었습니다. 관리자가 확인 후 처리하겠습니다.');
  };

  const handleSubscriptionCancel = () => {
    setIsCancellationWarning(true);
  };

  const confirmSubscriptionCancel = () => {
    // This would be implemented in the backend
    console.log('Subscription cancellation confirmed');
    setIsCancellationWarning(false);
    setIsManagingSubscription(false);
    alert('구독 취소가 요청되었습니다. 현재 결제 기간이 끝난 후 서비스가 중단됩니다.');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">프로필</h1>
          
          {/* User Info */}
          {user && (
            <div className="mb-8 p-6 bg-gray-50 rounded-lg">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">계정 정보</h2>
              </div>
              
              {isEditing ? (
                <div className="space-y-6">
                  {/* Avatar and Name */}
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={profileData.avatar || user.avatar || '/images/default-avatar.svg'}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="이름을 입력하세요"
                        required
                      />
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="010-1234-5678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">성별</label>
                      <select
                        value={profileData.gender}
                        onChange={(e) => setProfileData({...profileData, gender: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">성별을 선택하세요</option>
                        <option value="male">남성</option>
                        <option value="female">여성</option>
                        <option value="other">기타</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">생년월일</label>
                      <input
                        type="date"
                        value={profileData.dateOfBirth}
                        onChange={(e) => setProfileData({...profileData, dateOfBirth: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                    <textarea
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="전체 주소를 입력하세요"
                      rows="2"
                    />
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">자기소개</label>
                    <textarea
                      value={profileData.bio}
                      onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="자기소개를 입력하세요"
                      rows="3"
                      maxLength="500"
                    />
                    <p className="text-sm text-gray-500 mt-1">{profileData.bio.length}/500자</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2 pt-4">
                    <button
                      onClick={handleProfileUpdate}
                      className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      저장
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      <X className="w-4 h-4 mr-1" />
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">이름</p>
                      <p className="font-medium">{user.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">이메일</p>
                      <p className="font-medium">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">전화번호</p>
                      <p className="font-medium">{user.phone || '설정되지 않음'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">성별</p>
                      <p className="font-medium">
                        {user.gender === 'male' ? '남성' : 
                         user.gender === 'female' ? '여성' : 
                         user.gender === 'other' ? '기타' : '설정되지 않음'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">생년월일</p>
                      <p className="font-medium">
                        {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('ko-KR') : '설정되지 않음'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">역할</p>
                      <p className="font-medium capitalize">{user.role === 'instructor' ? '강사' : '학생'}</p>
                    </div>
                  </div>
                  
                  {/* Address */}
                  <div>
                    <p className="text-sm text-gray-600">주소</p>
                    <p className="font-medium">{user.address || '설정되지 않음'}</p>
                  </div>
                  
                  {/* Bio */}
                  {user.bio && (
                    <div>
                      <p className="text-sm text-gray-600">자기소개</p>
                      <p className="font-medium">{user.bio}</p>
                    </div>
                  )}
                  
                  {/* Edit Button */}
                  <div className="pt-4">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      프로필 수정
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-6">
            {user?.isFranchise && (
              <div className="border border-amber-200 rounded-lg p-6 bg-amber-50/30">
                <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-amber-700" />
                  가맹점 납부 현황
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  가맹점 등록일(
                  {franchiseBilling?.billingStartDate
                    ? formatBillingDate(franchiseBilling.billingStartDate)
                    : formatBillingDate(user.createdAt)}
                  )부터 매월 납부 내역입니다.
                </p>

                {loadingFranchiseBilling ? (
                  <p className="text-gray-500 py-6 text-center">납부 정보를 불러오는 중...</p>
                ) : !franchiseBilling ? (
                  <p className="text-gray-500 py-6 text-center">납부 정보를 불러올 수 없습니다.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <div className="bg-white rounded-lg border p-3">
                        <p className="text-xs text-gray-500">월 납부액</p>
                        <p className="text-lg font-semibold">{formatBillingCurrency(franchiseBilling.monthlyFee)}</p>
                      </div>
                      <div className="bg-white rounded-lg border p-3">
                        <p className="text-xs text-gray-500">납부 완료</p>
                        <p className="text-lg font-semibold text-green-700">{franchiseBilling.summary.paidCount}건</p>
                      </div>
                      <div className="bg-white rounded-lg border p-3">
                        <p className="text-xs text-gray-500">납부 대기</p>
                        <p className="text-lg font-semibold text-yellow-700">{franchiseBilling.summary.pendingCount}건</p>
                      </div>
                      <div className="bg-white rounded-lg border p-3">
                        <p className="text-xs text-gray-500">연체</p>
                        <p className="text-lg font-semibold text-red-700">{franchiseBilling.summary.overdueCount}건</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto bg-white border rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">청구월</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">납부액</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">납부 기한</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">납부일</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">비고</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {franchiseBilling.payments.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                아직 청구 내역이 없습니다.
                              </td>
                            </tr>
                          ) : (
                            [...franchiseBilling.payments].reverse().map((payment) => {
                              const statusMeta = getBillingStatusMeta(payment.status);
                              return (
                                <tr key={payment.period}>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{payment.label}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{formatBillingCurrency(payment.amount)}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{formatBillingDate(payment.dueDate)}</td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusMeta.className}`}>
                                      {statusMeta.label}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{formatBillingDate(payment.paidAt)}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{payment.note || '-'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Profile Management */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                프로필 관리
              </h2>
              <div className="space-y-3">

                <button 
                  onClick={() => setIsChangingEmail(true)}
                  className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                >
                  <span>이메일 주소 변경</span>
                  <Edit className="w-4 h-4 text-gray-400" />
                </button>
                
                <button className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md">
                  <span>자기소개 및 소셜 링크 업데이트</span>
                  <Edit className="w-4 h-4 text-gray-400" />
                </button>
                
              </div>
            </div>

            {/* Account Settings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                계정 설정
              </h2>
              <div className="space-y-3">
                <button 
                  onClick={() => setIsChangingPassword(true)}
                  className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                >
                  <span>비밀번호 변경</span>
                  <Lock className="w-4 h-4 text-gray-400" />
                </button>
                
                <button 
                  onClick={() => setIsManagingNotifications(true)}
                  className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                >
                  <span>알림 설정 관리</span>
                  <Bell className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={() => setIsDeletingAccount(true)}
                  className="flex items-center justify-between w-full p-3 text-left hover:bg-red-50 rounded-md text-red-600"
                >
                  <span>계정 삭제</span>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Learning Progress - Only for Students */}
            {user?.role === 'student' && (
              <div className="border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  학습 진행상황
                </h2>
                <div className="space-y-3">
                  <button 
                    onClick={() => setIsViewingProgress(true)}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>강의 진행상황 보기</span>
                    <Award className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => window.open('/certificates', '_blank')}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>수료증 다운로드</span>
                    <Download className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => window.open('/learning-history', '_blank')}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>학습 기록</span>
                    <Award className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => window.open('/achievements', '_blank')}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>성취 배지</span>
                    <Award className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            )}

            {/* Payment & Billing - Only for regular students */}
            {user?.role === 'student' && !user?.isFranchise && (
              <div className="border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  결제 및 청구
                </h2>
                <div className="space-y-3">
                  {/* Prominent Subscription Management Button for Students */}
                  <button 
                    onClick={() => setIsManagingSubscription(true)}
                    className="flex items-center justify-between w-full p-4 text-left bg-primary-50 hover:bg-primary-100 rounded-md border border-primary-200"
                  >
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 mr-3 text-primary-600" />
                      <div>
                        <span className="font-medium text-primary-800">구독 관리</span>
                        <div className="text-sm text-primary-600">현재 플랜: {subscriptionData.currentPlan.name}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-primary-600">₩{subscriptionData.currentPlan.price.toLocaleString()}/월</div>
                      <div className="text-xs text-gray-500">다음 결제: {subscriptionData.currentPlan.nextBilling}</div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => setIsViewingBilling(true)}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>결제 방법</span>
                    <CreditCard className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => window.open('/billing-history', '_blank')}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>청구 내역</span>
                    <CreditCard className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => window.open('/refund-request', '_blank')}
                    className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50 rounded-md"
                  >
                    <span>환불 요청</span>
                    <CreditCard className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Modals */}
          {/* Password Change Modal */}
          {isChangingPassword && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">비밀번호 변경</h3>
                <div className="space-y-4">
                  <input
                    type="password"
                    placeholder="현재 비밀번호"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                  <input
                    type="password"
                    placeholder="새 비밀번호"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                  <input
                    type="password"
                    placeholder="새 비밀번호 확인"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={handlePasswordChange}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    변경
                  </button>
                  <button
                    onClick={() => setIsChangingPassword(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          )}

          

          {/* Notification Settings Modal */}
          {isManagingNotifications && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">알림 설정</h3>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notificationSettings.emailNotifications}
                      onChange={(e) => setNotificationSettings({...notificationSettings, emailNotifications: e.target.checked})}
                      className="mr-2"
                    />
                    이메일 알림
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notificationSettings.courseUpdates}
                      onChange={(e) => setNotificationSettings({...notificationSettings, courseUpdates: e.target.checked})}
                      className="mr-2"
                    />
                    강의 업데이트 알림
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notificationSettings.promotionalEmails}
                      onChange={(e) => setNotificationSettings({...notificationSettings, promotionalEmails: e.target.checked})}
                      className="mr-2"
                    />
                    프로모션 이메일
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notificationSettings.pushNotifications}
                      onChange={(e) => setNotificationSettings({...notificationSettings, pushNotifications: e.target.checked})}
                      className="mr-2"
                    />
                    푸시 알림
                  </label>
                </div>
                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => setIsManagingNotifications(false)}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    저장
                  </button>
                  <button
                    onClick={() => setIsManagingNotifications(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          )}

          

                     {/* Email Change Modal */}
           {isChangingEmail && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 w-full max-w-md">
                 <h3 className="text-lg font-semibold mb-4">이메일 주소 변경</h3>
                 <div className="space-y-4">
                   <input
                     type="password"
                     placeholder="현재 비밀번호"
                     value={emailData.currentPassword}
                     onChange={(e) => setEmailData({...emailData, currentPassword: e.target.value})}
                     className="w-full p-2 border border-gray-300 rounded-md"
                   />
                   <input
                     type="email"
                     placeholder="새 이메일 주소"
                     value={emailData.newEmail}
                     onChange={(e) => setEmailData({...emailData, newEmail: e.target.value})}
                     className="w-full p-2 border border-gray-300 rounded-md"
                   />
                 </div>
                 <div className="flex space-x-2 mt-4">
                   <button
                     onClick={handleEmailChange}
                     className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                   >
                     변경
                   </button>
                   <button
                     onClick={() => setIsChangingEmail(false)}
                     className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                   >
                     취소
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* Learning Progress Modal */}
           {isViewingProgress && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                 <h3 className="text-lg font-semibold mb-4">학습 진행상황</h3>
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                   <div className="text-center p-4 bg-blue-50 rounded-lg">
                     <div className="text-2xl font-bold text-blue-600">{learningProgress.completedCourses}</div>
                     <div className="text-sm text-gray-600">완료한 강의</div>
                   </div>
                   <div className="text-center p-4 bg-green-50 rounded-lg">
                     <div className="text-2xl font-bold text-green-600">{learningProgress.totalHours}</div>
                     <div className="text-sm text-gray-600">총 학습 시간</div>
                   </div>
                   <div className="text-center p-4 bg-yellow-50 rounded-lg">
                     <div className="text-2xl font-bold text-yellow-600">{learningProgress.certificates}</div>
                     <div className="text-sm text-gray-600">수료증</div>
                   </div>
                   <div className="text-center p-4 bg-purple-50 rounded-lg">
                     <div className="text-2xl font-bold text-purple-600">{learningProgress.achievements}</div>
                     <div className="text-sm text-gray-600">성취 배지</div>
                   </div>
                 </div>
                 <div className="space-y-4">
                   <h4 className="font-semibold">최근 학습한 강의</h4>
                   <div className="space-y-2">
                     <div className="p-3 border rounded-lg text-center text-gray-500">
                       아직 수강한 강의가 없습니다.
                     </div>
                   </div>
                 </div>
                 <div className="flex justify-end mt-6">
                   <button
                     onClick={() => setIsViewingProgress(false)}
                     className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                   >
                     닫기
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* Billing Modal */}
           {isViewingBilling && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                 <h3 className="text-lg font-semibold mb-4">결제 방법</h3>
                 <div className="space-y-4">
                   <div>
                     <h4 className="font-semibold mb-2">등록된 결제 방법</h4>
                     {billingInfo.paymentMethods.map(method => (
                       <div key={method.id} className="p-3 border rounded-lg flex items-center justify-between">
                         <div>
                           <div className="font-medium">{method.type}</div>
                           <div className="text-sm text-gray-600">**** **** **** {method.last4}</div>
                         </div>
                         <div className="text-sm text-gray-500">만료: {method.expiry}</div>
                       </div>
                     ))}
                   </div>
                   <div>
                     <h4 className="font-semibold mb-2">최근 거래 내역</h4>
                     {billingInfo.recentTransactions.map(transaction => (
                       <div key={transaction.id} className="p-3 border rounded-lg flex items-center justify-between">
                         <div>
                           <div className="font-medium">{transaction.course}</div>
                           <div className="text-sm text-gray-600">{transaction.date}</div>
                         </div>
                         <div className="text-right">
                           <div className="font-medium">₩{transaction.amount.toLocaleString()}</div>
                           <div className="text-sm text-green-600">{transaction.status}</div>
                         </div>
                       </div>
                     ))}
                   </div>
                   <div>
                     <h4 className="font-semibold mb-2">구독 정보</h4>
                     <div className="p-3 border rounded-lg">
                       <div className="flex items-center justify-between">
                         <div>
                           <div className="font-medium">{billingInfo.subscription.plan} 구독</div>
                           <div className="text-sm text-gray-600">다음 결제일: {billingInfo.subscription.nextBilling}</div>
                         </div>
                         <div className="text-right">
                           <div className="font-medium">₩{billingInfo.subscription.amount.toLocaleString()}/월</div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
                 <div className="flex justify-end mt-6">
                   <button
                     onClick={() => setIsViewingBilling(false)}
                     className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                   >
                     닫기
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* Account Deletion Modal */}
           {isDeletingAccount && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 w-full max-w-md">
                 <h3 className="text-lg font-semibold mb-4 text-red-600">계정 삭제</h3>
                 <p className="text-gray-600 mb-4">
                   정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없으며, 모든 데이터가 영구적으로 삭제됩니다.
                 </p>
                 <div className="flex space-x-2">
                   <button
                     onClick={handleAccountDeletion}
                     className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                   >
                     삭제
                   </button>
                   <button
                     onClick={() => setIsDeletingAccount(false)}
                     className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                   >
                     취소
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* Subscription Management Modal */}
           {isManagingSubscription && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                 <div className="flex items-center justify-between mb-6">
                   <h3 className="text-2xl font-semibold text-gray-900">구독 관리</h3>
                   <button
                     onClick={() => setIsManagingSubscription(false)}
                     className="text-gray-400 hover:text-gray-600"
                   >
                     <X className="w-6 h-6" />
                   </button>
                 </div>

                 {/* Current Plan */}
                 <div className="mb-8">
                   <h4 className="text-lg font-semibold mb-4 flex items-center">
                     <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                     현재 구독 플랜
                   </h4>
                   <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                     <div className="flex items-center justify-between mb-4">
                       <div>
                         <h5 className="text-xl font-semibold text-green-800">{subscriptionData.currentPlan.name}</h5>
                         <p className="text-green-600">₩{subscriptionData.currentPlan.price.toLocaleString()}/월</p>
                       </div>
                       <div className="text-right">
                         <div className="text-sm text-green-600">상태: 활성</div>
                         <div className="text-sm text-gray-500">다음 결제: {subscriptionData.currentPlan.nextBilling}</div>
                       </div>
                     </div>
                     <div className="space-y-2">
                       {subscriptionData.currentPlan.features.map((feature, index) => (
                         <div key={index} className="flex items-center text-sm text-green-700">
                           <CheckCircle className="w-4 h-4 mr-2" />
                           {feature}
                         </div>
                       ))}
                     </div>
                   </div>
                 </div>

                 {/* Available Plans */}
                 <div className="mb-8">
                   <h4 className="text-lg font-semibold mb-4">구독 플랜 변경</h4>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                     {subscriptionData.availablePlans.map((plan) => (
                       <div key={plan.id} className={`border rounded-lg p-6 ${plan.popular ? 'border-primary-300 bg-primary-50' : 'border-gray-200'}`}>
                         {plan.popular && (
                           <div className="bg-primary-600 text-white text-xs font-medium px-3 py-1 rounded-full inline-block mb-3">
                             인기
                           </div>
                         )}
                         <h5 className="text-lg font-semibold mb-2">{plan.name}</h5>
                         <div className="text-2xl font-bold mb-4">
                           ₩{plan.price.toLocaleString()}
                           <span className="text-sm font-normal text-gray-500">/월</span>
                         </div>
                         <div className="space-y-2 mb-6">
                           {plan.features.map((feature, index) => (
                             <div key={index} className="flex items-center text-sm text-gray-600">
                               <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                               {feature}
                             </div>
                           ))}
                         </div>
                         <button
                           onClick={() => handleSubscriptionChange(plan.id)}
                           className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                             plan.id === 'monthly' 
                               ? 'bg-primary-600 text-white hover:bg-primary-700' 
                               : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                           }`}
                         >
                           {plan.id === 'monthly' ? '현재 플랜' : '플랜 변경'}
                         </button>
                       </div>
                     ))}
                   </div>
                 </div>

                 {/* Subscription Actions */}
                 <div className="border-t pt-6">
                   <h4 className="text-lg font-semibold mb-4">구독 관리</h4>
                   <div className="space-y-4">
                     <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                       <div>
                         <h5 className="font-semibold text-yellow-800">구독 일시정지</h5>
                         <p className="text-sm text-yellow-600">최대 3개월까지 구독을 일시정지할 수 있습니다.</p>
                       </div>
                       <button className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700">
                         일시정지
                       </button>
                     </div>
                     
                     <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                       <div>
                         <h5 className="font-semibold text-red-800">구독 취소</h5>
                         <p className="text-sm text-red-600">구독 취소 시 현재 결제 기간이 끝난 후 서비스가 중단됩니다.</p>
                       </div>
                       <button 
                         onClick={handleSubscriptionCancel}
                         className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                       >
                         구독 취소
                       </button>
                     </div>
                   </div>
                 </div>

                 {/* Billing Information */}
                 <div className="border-t pt-6 mt-6">
                   <h4 className="text-lg font-semibold mb-4">결제 정보</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="p-4 border rounded-lg">
                       <h5 className="font-semibold mb-2">결제 방법</h5>
                       <div className="flex items-center">
                         <CreditCard className="w-5 h-5 mr-2 text-gray-400" />
                         <span>**** **** **** 1234</span>
                       </div>
                       <p className="text-sm text-gray-500 mt-1">만료: 12/25</p>
                     </div>
                     <div className="p-4 border rounded-lg">
                       <h5 className="font-semibold mb-2">다음 결제</h5>
                       <div className="flex items-center">
                         <Calendar className="w-5 h-5 mr-2 text-gray-400" />
                         <span>{subscriptionData.currentPlan.nextBilling}</span>
                       </div>
                       <p className="text-sm text-gray-500 mt-1">₩{subscriptionData.currentPlan.price.toLocaleString()}</p>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           )}

           {/* Subscription Cancellation Warning Modal */}
           {isCancellationWarning && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 w-full max-w-md">
                 <div className="flex items-center mb-4">
                   <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                     <Trash2 className="w-6 h-6 text-red-600" />
                   </div>
                   <div>
                     <h3 className="text-lg font-semibold text-red-600">구독 취소</h3>
                     <p className="text-sm text-gray-500">이 작업은 되돌릴 수 없습니다</p>
                   </div>
                 </div>
                 
                 <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                   <h4 className="font-semibold text-red-800 mb-2">구독 취소 시 주의사항:</h4>
                   <ul className="text-sm text-red-700 space-y-1">
                     <li>• 현재 결제 기간이 끝난 후 서비스가 중단됩니다</li>
                     <li>• 진행 중인 강의에 대한 접근이 제한됩니다</li>
                     <li>• 수료증 발급이 중단됩니다</li>
                     <li>• 구독 재개 시 새로운 결제가 필요합니다</li>
                   </ul>
                 </div>
                 
                 <p className="text-gray-600 mb-6">
                   정말로 구독을 취소하시겠습니까?
                 </p>
                 
                 <div className="flex space-x-3">
                   <button
                     onClick={confirmSubscriptionCancel}
                     className="flex-1 px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium transition-colors duration-200"
                   >
                     구독 취소
                   </button>
                   <button
                     onClick={() => setIsCancellationWarning(false)}
                     className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium transition-colors duration-200"
                   >
                     유지하기
                   </button>
                 </div>
               </div>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 